import {
  Delete as DeleteIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

const ContactManagement = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedContact, setSelectedContact] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [filters, setFilters] = useState({
    status: '',
    department: '',
  });

  const fetchContacts = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        ...filters,
      });

      const response = await axios.get(`${API_URL}/contacts?${params}`);

      if (response.data.success) {
        setContacts(response.data.data.contacts);
      } else {
        setError(response.data.message || 'Failed to fetch contacts');
      }
    } catch (error) {
      console.error('Error fetching contacts:', error);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContacts();
  }, [filters]);

  const handleViewContact = (contact) => {
    setSelectedContact(contact);
    setViewDialogOpen(true);
  };

  const handleDeleteContact = (contact) => {
    setSelectedContact(contact);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    try {
      const response = await axios.delete(
        `${API_URL}/contacts/${selectedContact?._id}`
      );

      if (response.data.success) {
        fetchContacts();
        setDeleteDialogOpen(false);
        setSelectedContact(null);
      } else {
        setError(response.data.message || 'Failed to delete contact');
      }
    } catch (error) {
      console.error('Error deleting contact:', error);
      setError('Network error. Please try again.');
    }
  };

  const handleStatusUpdate = async (contactId, newStatus) => {
    try {
      const response = await axios.patch(
        `${API_URL}/contacts/${contactId}/status`,
        {
          status: newStatus,
        }
      );

      if (response.data.success) {
        fetchContacts();
      } else {
        setError(response.data.message || 'Failed to update status');
      }
    } catch (error) {
      console.error('Error updating status:', error);
      setError('Network error. Please try again.');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'warning';
      case 'read':
        return 'info';
      case 'replied':
        return 'success';
      default:
        return 'default';
    }
  };

  const getDepartmentLabel = (department) => {
    switch (department) {
      case 'info.npico@alhazaagroup.com':
        return 'Factory Management';
      case 'sales.npico@alhazaagroup.com':
        return 'Sales';
      case 'hr.npico@alhazaagroup.com':
        return 'Human Resources';
      default:
        return department;
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  if (loading && contacts?.length === 0) {
    return (
      <Box
        display='flex'
        justifyContent='center'
        alignItems='center'
        minHeight='400px'
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Box
        sx={{
          mb: 4,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography variant='h4' component='h1'>
          Contact Management
        </Typography>
      </Box>

      {error && (
        <Alert severity='error' sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Department</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {contacts?.map?.((contact) => (
              <TableRow key={contact?._id} hover>
                <TableCell>{contact?.fullName}</TableCell>
                <TableCell>{contact?.email}</TableCell>
                <TableCell>{getDepartmentLabel(contact?.department)}</TableCell>
                <TableCell>
                  <FormControl size='small'>
                    <Select
                      value={contact?.status}
                      onChange={(e) =>
                        handleStatusUpdate(contact?._id, e.target.value)
                      }
                      sx={{ minWidth: 100 }}
                    >
                      <MenuItem value='pending'>Pending</MenuItem>
                      <MenuItem value='read'>Read</MenuItem>
                      <MenuItem value='replied'>Replied</MenuItem>
                    </Select>
                  </FormControl>
                </TableCell>
                <TableCell>{formatDate(contact?.createdAt)}</TableCell>
                <TableCell>
                  <Tooltip title='View Details'>
                    <IconButton
                      onClick={() => handleViewContact(contact)}
                      size='small'
                    >
                      <VisibilityIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title='Delete'>
                    <IconButton
                      onClick={() => handleDeleteContact(contact)}
                      color='error'
                      size='small'
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* View Contact Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth='md'
        fullWidth
      >
        <DialogTitle>Contact Details</DialogTitle>
        <DialogContent>
          {selectedContact && (
            <Box sx={{ mt: 2 }}>
              <Typography variant='h6' gutterBottom>
                Contact Information
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Typography variant='subtitle2' color='text.secondary'>
                  Name
                </Typography>
                <Typography variant='body1'>
                  {selectedContact?.fullName}
                </Typography>
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant='subtitle2' color='text.secondary'>
                  Email
                </Typography>
                <Typography variant='body1'>
                  {selectedContact?.email}
                </Typography>
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant='subtitle2' color='text.secondary'>
                  Department
                </Typography>
                <Typography variant='body1'>
                  {getDepartmentLabel(selectedContact?.department)}
                </Typography>
              </Box>
              {selectedContact?.address && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant='subtitle2' color='text.secondary'>
                    Address
                  </Typography>
                  <Typography variant='body1'>
                    {selectedContact?.address}
                  </Typography>
                </Box>
              )}
              <Box sx={{ mb: 2 }}>
                <Typography variant='subtitle2' color='text.secondary'>
                  Message
                </Typography>
                <Typography variant='body1' sx={{ whiteSpace: 'pre-wrap' }}>
                  {selectedContact?.message}
                </Typography>
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant='subtitle2' color='text.secondary'>
                  Status
                </Typography>
                <Chip
                  label={selectedContact?.status}
                  color={getStatusColor(selectedContact?.status)}
                  size='small'
                />
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant='subtitle2' color='text.secondary'>
                  Submitted
                </Typography>
                <Typography variant='body1'>
                  {formatDate(selectedContact?.createdAt)}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this contact from{' '}
            {selectedContact?.fullName}? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={confirmDelete} color='error' variant='contained'>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Filter Dialog */}
      <Dialog
        open={filterDialogOpen}
        onClose={() => setFilterDialogOpen(false)}
      >
        <DialogTitle>Filter Contacts</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.status}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, status: e.target.value }))
                }
                label='Status'
              >
                <MenuItem value=''>All</MenuItem>
                <MenuItem value='pending'>Pending</MenuItem>
                <MenuItem value='read'>Read</MenuItem>
                <MenuItem value='replied'>Replied</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Department</InputLabel>
              <Select
                value={filters.department}
                onChange={(e) =>
                  setFilters((prev) => ({
                    ...prev,
                    department: e.target.value,
                  }))
                }
                label='Department'
              >
                <MenuItem value=''>All</MenuItem>
                <MenuItem value='info.npico@alhazaagroup.com'>
                  Factory Management
                </MenuItem>
                <MenuItem value='sales.npico@alhazaagroup.com'>Sales</MenuItem>
                <MenuItem value='hr.npico@alhazaagroup.com'>
                  Human Resources
                </MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFilters({ status: '', department: '' })}>
            Clear Filters
          </Button>
          <Button onClick={() => setFilterDialogOpen(false)}>Apply</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ContactManagement;
